import { useEffect, useState } from "react";

/**
 * Resovia splash — vises kun ved cold start af PWA'en.
 * Kombinerer:
 *  - B) Grøn bølge der ruller ind fra højre
 *  - D) Liquid blob der morpher omkring logoet + typewriter-effekt på "Resovia"-teksten
 *
 * Logic:
 *  - sessionStorage flag forhindrer splash ved warm resume (tab-skift i mobil multitasking)
 *  - Vises ~2.2 sek, derefter fader væk og kalder onDone()
 */
export default function SplashScreen({ onDone }) {
  const [stage, setStage] = useState("enter"); // enter → hold → exit
  const fullText = "Resovia";
  const [typed, setTyped] = useState("");

  useEffect(() => {
    // Typewriter
    let i = 0;
    const typeTimer = setInterval(() => {
      i += 1;
      setTyped(fullText.slice(0, i));
      if (i >= fullText.length) clearInterval(typeTimer);
    }, 110);

    const holdTimer = setTimeout(() => setStage("hold"), 1800);
    const exitTimer = setTimeout(() => setStage("exit"), 2800);
    const doneTimer = setTimeout(() => onDone && onDone(), 3300);

    return () => {
      clearInterval(typeTimer);
      clearTimeout(holdTimer);
      clearTimeout(exitTimer);
      clearTimeout(doneTimer);
    };
  }, [onDone]);

  return (
    <div
      data-testid="splash-screen"
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 99999,
        background: "linear-gradient(160deg, #0f1f17 0%, #1a3a28 50%, #224a32 100%)",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        overflow: "hidden",
        opacity: stage === "exit" ? 0 : 1,
        transition: "opacity 500ms ease-out",
        pointerEvents: stage === "exit" ? "none" : "auto",
      }}
    >
      {/* Bølge der ruller ind fra højre — placeret nederst så den ikke skjuler træet */}
      <svg
        viewBox="0 0 1200 800"
        preserveAspectRatio="none"
        style={{
          position: "absolute",
          left: 0,
          right: 0,
          bottom: 0,
          width: "100%",
          height: "60%",
          opacity: 0.5,
          animation: "splashWaveSlide 1.2s cubic-bezier(0.22, 1, 0.36, 1) forwards",
        }}
        aria-hidden="true"
      >
        <defs>
          <linearGradient id="splashWaveGrad" x1="0" y1="0" x2="1" y2="1">
            <stop offset="0%" stopColor="#86efac" stopOpacity="0.5" />
            <stop offset="100%" stopColor="#22c55e" stopOpacity="0.2" />
          </linearGradient>
        </defs>
        <path
          d="M -100 400 Q 250 250 600 380 T 1300 360 L 1300 900 L -100 900 Z"
          fill="url(#splashWaveGrad)"
        />
        <path
          d="M -100 500 Q 350 380 700 480 T 1400 450 L 1400 900 L -100 900 Z"
          fill="url(#splashWaveGrad)"
          opacity="0.6"
        />
      </svg>

      {/* Liquid blob bag logo - bruger SVG path med animated morph */}
      <div
        style={{
          position: "relative",
          width: "min(280px, 65vw)",
          height: "min(280px, 65vw)",
          marginBottom: "40px",
        }}
      >
        {/* Subtil glow bagved */}
        <div
          style={{
            position: "absolute",
            inset: "-10%",
            background: "radial-gradient(circle at center, rgba(134,239,172,0.25) 0%, rgba(134,239,172,0) 65%)",
            animation: "splashGlow 3s ease-in-out infinite",
            pointerEvents: "none",
          }}
        />

        {/* Træ med ressource-flow */}
        <svg
          viewBox="0 0 200 200"
          style={{
            position: "absolute",
            inset: 0,
            width: "100%",
            height: "100%",
            filter: "drop-shadow(0 8px 24px rgba(134, 239, 172, 0.25))",
            animation: "splashTreeIn 700ms cubic-bezier(0.22, 1, 0.36, 1) 100ms both",
          }}
          aria-hidden="true"
        >
          <defs>
            <linearGradient id="trunkGrad" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="#dcfce7" />
              <stop offset="100%" stopColor="#86efac" />
            </linearGradient>
            <linearGradient id="branchGrad" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="#f0fdf4" />
              <stop offset="100%" stopColor="#86efac" />
            </linearGradient>
            <radialGradient id="nodeGrad">
              <stop offset="0%" stopColor="#ffffff" />
              <stop offset="50%" stopColor="#bbf7d0" />
              <stop offset="100%" stopColor="#22c55e" />
            </radialGradient>
          </defs>

          {/* Stamme */}
          <line x1="100" y1="175" x2="100" y2="120" stroke="#86efac" strokeWidth="5" strokeLinecap="round" />

          {/* 3 grene fra toppen af stammen til hver krone-node */}
          <path d="M 100 120 Q 100 90 100 60" stroke="#86efac" strokeWidth="4" strokeLinecap="round" fill="none" />
          <path d="M 100 120 Q 80 110 60 92" stroke="#86efac" strokeWidth="4" strokeLinecap="round" fill="none" />
          <path d="M 100 120 Q 120 110 140 92" stroke="#86efac" strokeWidth="4" strokeLinecap="round" fill="none" />

          {/* Flow-linjer der forbinder kronens 3 noder (ressource-flow-hint) */}
          <g style={{ animation: "splashFlowFade 900ms ease-out 1100ms both" }}>
            <path d="M 100 60 Q 130 70 140 92" stroke="#dcfce7" strokeWidth="1.6" strokeLinecap="round" strokeDasharray="3 4" fill="none" opacity="0.7">
              <animate attributeName="stroke-dashoffset" from="0" to="-28" dur="6s" repeatCount="indefinite" />
            </path>
            <path d="M 140 92 Q 100 110 60 92" stroke="#dcfce7" strokeWidth="1.6" strokeLinecap="round" strokeDasharray="3 4" fill="none" opacity="0.7">
              <animate attributeName="stroke-dashoffset" from="0" to="-28" dur="6s" repeatCount="indefinite" />
            </path>
            <path d="M 60 92 Q 70 70 100 60" stroke="#dcfce7" strokeWidth="1.6" strokeLinecap="round" strokeDasharray="3 4" fill="none" opacity="0.7">
              <animate attributeName="stroke-dashoffset" from="0" to="-28" dur="6s" repeatCount="indefinite" />
            </path>
          </g>

          {/* 3 krone-noder — personale, biler, affald */}
          <circle cx="100" cy="60" r="11" fill="#bbf7d0" stroke="#fff" strokeWidth="1.5" />
          <circle cx="60" cy="92" r="11" fill="#bbf7d0" stroke="#fff" strokeWidth="1.5" />
          <circle cx="140" cy="92" r="11" fill="#bbf7d0" stroke="#fff" strokeWidth="1.5" />

          {/* Ring-pulse omkring centrum-noden (subtilt) */}
          <circle cx="100" cy="60" r="11" fill="none" stroke="#86efac" strokeWidth="1.5" opacity="0">
            <animate attributeName="r" from="11" to="28" begin="1.6s;ring.end+1s" dur="2s" fill="freeze" id="ring" />
            <animate attributeName="opacity" values="0.9;0" begin="1.6s;ring.end+1s" dur="2s" fill="freeze" />
          </circle>
        </svg>
      </div>

      {/* Typewriter-titel */}
      <div
        style={{
          fontFamily: "'Bebas Neue', -apple-system, system-ui, sans-serif",
          fontSize: "clamp(36px, 9vw, 64px)",
          letterSpacing: "0.08em",
          color: "#f0fdf4",
          textTransform: "uppercase",
          fontWeight: 700,
          minHeight: "1.2em",
          textShadow: "0 2px 30px rgba(134, 239, 172, 0.3)",
        }}
      >
        {typed}
        <span
          style={{
            display: "inline-block",
            width: "0.08em",
            height: "0.9em",
            background: "#86efac",
            marginLeft: "0.05em",
            verticalAlign: "middle",
            animation: typed.length < fullText.length ? "splashCaret 0.4s steps(2) infinite" : "none",
            opacity: typed.length < fullText.length ? 1 : 0,
            transition: "opacity 200ms",
          }}
        />
      </div>

      {/* Tagline */}
      <div
        style={{
          marginTop: "12px",
          fontSize: "11px",
          letterSpacing: "0.3em",
          color: "rgba(187, 247, 208, 0.7)",
          textTransform: "uppercase",
          fontWeight: 500,
          opacity: stage === "enter" ? 0 : 1,
          transition: "opacity 600ms ease-in 200ms",
        }}
      >
        Festival Logistics
      </div>

      <style>{`
        @keyframes splashWaveSlide {
          from { transform: translateX(60%); opacity: 0; }
          to { transform: translateX(0); opacity: 0.45; }
        }
        @keyframes splashCaret {
          50% { opacity: 0; }
        }
        @keyframes splashGlow {
          0%, 100% { transform: scale(1); opacity: 0.7; }
          50% { transform: scale(1.08); opacity: 1; }
        }
        @keyframes splashTreeIn {
          0%   { opacity: 0; transform: translateY(8px) scale(0.85); }
          100% { opacity: 1; transform: translateY(0)   scale(1); }
        }
        @keyframes splashNodePop {
          0%   { transform: scale(0); }
          70%  { transform: scale(1.25); }
          100% { transform: scale(1); }
        }
        @keyframes splashFlowFade {
          from { opacity: 0; }
          to   { opacity: 1; }
        }
        @keyframes splashPulseRing {
          0%   { transform: scale(1);   opacity: 0.8; }
          80%  { transform: scale(2.6); opacity: 0; }
          100% { transform: scale(2.6); opacity: 0; }
        }
      `}</style>
    </div>
  );
}
